import React, { useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import moment from "moment";
import InfiniteScroll from "react-infinite-scroll-component";

// Interfaces
import { AdminUser } from "../../../../interfaces/AdminUser";

// Custom components
import SearchBar from "../../../general/SearchBar";
import UserDialog from "./UserDialog";

// Hooks
import { useFetchUser } from "../../../../hooks/useFetchUser";

// Settings
import settings from "../../../../settings.json";

// Firebase
import { firestore } from "../../../../firebase/firebase";
import {
    collection,
    query,
    orderBy,
    startAfter,
    limit,
    QueryDocumentSnapshot,
    DocumentData,
    getDocs,
    getCountFromServer,
} from "firebase/firestore";
import { genDoc } from "../../../../utils/firebase";

// Mui
import { Button, Grid } from "@mui/material";

const COLLECTION = "Users";

const Users: React.FC = () => {
    const hist = useHistory();
    const { currentAdmin, setLoading } = useFetchUser();

    // States
    const [users, setUsers] = useState<AdminUser[]>([]);
    const [filteredItems, setFilteredItems] = useState<AdminUser[]>([]);
    const [count, setCount] = useState<number>(0);
    const [lastVisible, setLastVisible] = useState<QueryDocumentSnapshot<DocumentData> | null>(null);
    const [searchQuery, setSearchQuery] = useState("");
    const [open, setOpen] = useState(false);

    const allUsersCache = useRef<AdminUser[] | null>(null);
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const fetchFirstItems = async () => {
        try {
            const localQuery = query(collection(firestore, COLLECTION), orderBy("createdAt", "desc"), limit(15));
            const snapshot = await getDocs(localQuery);
            const data = snapshot.docs.map(genDoc<AdminUser>()).filter((item: AdminUser) => {
                if (currentAdmin && currentAdmin.roles.includes(settings.app.highestRole)) return true;
                return !item.isDeleted;
            });
            setLastVisible(snapshot.docs[snapshot.docs.length - 1] ?? null);
            setUsers(data);
            setFilteredItems(data);
        } catch (e) {
            console.error(e);
        }
    };

    const fetchCount = async () => {
        try {
            const snapshot = await getCountFromServer(collection(firestore, COLLECTION));
            setCount(snapshot.data().count);
        } catch (e) {
            console.error(e);
        }
    };

    const fetchMoreItems = async () => {
        if (!lastVisible) return;
        try {
            const localQuery = query(
                collection(firestore, COLLECTION),
                orderBy("createdAt", "desc"),
                limit(settings.page.rowsPerPage),
                startAfter(lastVisible)
            );
            const payload = await getDocs(localQuery);
            if (payload.docs.length === 0) return;
            const data = payload.docs.map(genDoc<AdminUser>()).filter((item: AdminUser) => {
                if (currentAdmin && currentAdmin.roles.includes(settings.app.highestRole)) return true;
                return !item.isDeleted;
            });
            setLastVisible(payload.docs[payload.docs.length - 1]);
            setUsers(prev => [...prev, ...data]);
            setFilteredItems(prev => [...prev, ...data]);
        } catch (e) {
            console.error(e);
        }
    };

    const handleSearch = (value: string) => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => setSearchQuery(value), 300);
    };

    const filterItems = async (q: string) => {
        if (!allUsersCache.current) {
            const snapshot = await getDocs(collection(firestore, COLLECTION));
            allUsersCache.current = snapshot.docs.map(genDoc<AdminUser>());
        }
        const lower = q.toLowerCase().trim();
        const filtered = allUsersCache.current.filter(x => {
            if (!x.firstName || !x.lastName || !x.email || !x.createdAt) return false;
            return (
                x.email.toLowerCase().includes(lower) ||
                x.firstName.toLowerCase().includes(lower) ||
                x.lastName.toLowerCase().includes(lower) ||
                moment(x.createdAt).format("DD-MM-YYYY HH:mm:ss").includes(lower)
            );
        });
        setCount(filtered.length);
        setFilteredItems(filtered.sort((a, b) => (a.createdAt && b.createdAt ? a.createdAt.getTime() - b.createdAt.getTime() : -1)));
    };

    useEffect(() => {
        if (searchQuery) {
            filterItems(searchQuery);
        } else {
            setFilteredItems(users);
            fetchCount();
        }
    }, [searchQuery]); // eslint-disable-line

    useEffect(() => {
        setLoading(true);
        Promise.all([fetchCount(), fetchFirstItems()]).finally(() => setLoading(false));
    }, []); // eslint-disable-line

    return (
        <>
            <SearchBar onSearch={handleSearch}>
                <div className="searchbar__button">
                    <Button variant="contained" color="primary" onClick={() => setOpen(true)}>
                        Add
                    </Button>
                </div>
            </SearchBar>

            <div id="scrollableDiv" className="scrollable__container">
                <InfiniteScroll
                    dataLength={filteredItems.length}
                    next={searchQuery ? () => undefined : fetchMoreItems}
                    hasMore={!searchQuery && filteredItems.length < count}
                    loader={<h4 className="infinitescroll__container__message">Fetching next items...</h4>}
                    endMessage={!filteredItems.length && <h4 className="infinitescroll__container__message">No data</h4>}
                    className="infinitescroll__container"
                    scrollableTarget="scrollableDiv"
                    height={count > 15 ? "calc(100vh - 278px)" : ""}
                >
                    {filteredItems.map(x => (
                        <div key={x.id} className="infinitescroll__container__card" onClick={() => hist.push(`/dashboard/admin/${x.id}/0`)}>
                            <Grid item container>
                                <Grid item xs={12} sm={4}>
                                    <div className="centered__parent">
                                        <div>
                                            <div className="centered__parent">
                                                <div className="infinitescroll__container__colname">Firstname</div>
                                            </div>
                                            <div className="centered__parent">
                                                <div className="infinitescroll__container__text">{x.firstName}</div>
                                            </div>
                                        </div>
                                    </div>
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <div className="centered__parent">
                                        <div>
                                            <div className="centered__parent">
                                                <div className="infinitescroll__container__colname">Lastname</div>
                                            </div>
                                            <div className="centered__parent">
                                                <span className="infinitescroll__container__text">{x.lastName}</span>
                                            </div>
                                        </div>
                                    </div>
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <div className="centered__parent">
                                        <div>
                                            <div className="centered__parent">
                                                <div className="infinitescroll__container__colname">Email</div>
                                            </div>
                                            <div className="centered__parent">
                                                <div className="infinitescroll__container__text">{x.email}</div>
                                            </div>
                                        </div>
                                    </div>
                                </Grid>
                            </Grid>
                        </div>
                    ))}
                </InfiniteScroll>
            </div>

            {open && <UserDialog open={open} setOpen={setOpen} userId={""} onSuccess={() => { fetchFirstItems(); fetchCount(); }} />}
        </>
    );
};

export default Users;
